import { Avatar, brandAvatarTint } from '@/components/ui/avatar';
import { MenuContent, MenuItem, MenuSeparator } from '@/components/ui/menu';
import { useAuth } from '@/contexts/auth-context';
import { useSwitchBrandAction } from '@/hooks/actions/use-switch-brand-action';
import { useOrganizationBrandsQuery } from '@/hooks/queries/use-organization-brands-query';
import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import { usePlanGates } from '@/hooks/use-plan-gates';
import { Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { FeatureFlag, PlanLimit } from '@fluenceur/types';
import { Check, Plus, Settings, UserPlus } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface BrandMenuProps {
  onCreateBrand: () => void;
}

const BrandMenu: React.FC<BrandMenuProps> = ({ onCreateBrand }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const brand = user?.currentBrand;

  const { data: brands, isLoading: isBrandsLoading, isError } = useOrganizationBrandsQuery();
  const switchBrandMutation = useSwitchBrandAction();
  const { hasFeature, isLimitExceeded } = usePlanFeaturesQuery();
  const { openUpsell } = usePlanGates();
  const canAddBrand = hasFeature(FeatureFlag.MULTI_BRAND) && !isLimitExceeded('maxBrands');
  const canInviteMembers = hasFeature(FeatureFlag.TEAM_MEMBERS);

  const handleBrandSwitch = (brandId: string) => {
    if (brandId !== brand?.id) {
      switchBrandMutation.mutate(brandId);
    }
  };

  const handleBrandSettings = () => {
    navigate('/settings');
  };

  const handleInviteMembers = () => {
    if (canInviteMembers) {
      navigate('/settings/team');
    } else {
      openUpsell(PlanLimit.TEAM_MEMBERS);
    }
  };

  return (
    <MenuContent minW={270} rounded="xl">
      {/* Current Brand Header */}
      <Flex align="center" gap={3} p={1}>
        <Avatar
          src={brand?.imageUrl || undefined}
          size="xs"
          name={brand?.name}
          shape="square"
          rounded="lg"
          {...brandAvatarTint}
        />
        <VStack align="flex-start" gap={0}>
          <Text fontWeight="semibold" fontSize="14px" color="gray.900">
            {brand?.name || 'Fluenceur'}
          </Text>
          <Text
            fontSize="12px"
            color="gray.500"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            maxW="120px"
          >
            {t('brandMenu.currentBrand')}
          </Text>
        </VStack>
      </Flex>
      <MenuSeparator />

      {/* Brand Selection List */}
      {isBrandsLoading ? (
        <Flex justify="center" py={3}>
          <Spinner size="sm" />
        </Flex>
      ) : isError ? (
        <Flex justify="center" py={3}>
          <Text fontSize="sm" color="red.500">
            {t('brandMenu.loadError')}
          </Text>
        </Flex>
      ) : (
        brands?.map((orgBrand) => (
          <MenuItem
            key={orgBrand.id}
            rounded="lg"
            value={`select-brand-${orgBrand.id}`}
            cursor="pointer"
            onClick={() => handleBrandSwitch(orgBrand.id)}
            disabled={switchBrandMutation.isPending}
          >
            <Flex align="center" justify="space-between" w="full">
              <Flex align="center" gap={3}>
                <Avatar
                  src={orgBrand.imageUrl || undefined}
                  size="2xs"
                  name={orgBrand.name}
                  shape="square"
                  rounded="sm"
                  {...brandAvatarTint}
                  boxSize={4}
                />
                <Text fontWeight="medium" fontSize="14px" color="gray.900">
                  {orgBrand.name}
                </Text>
              </Flex>
              {orgBrand.isCurrent && <Check width={16} height={16} />}
            </Flex>
          </MenuItem>
        ))
      )}

      <MenuSeparator />

      {/* Menu Actions */}
      {canAddBrand && (
        <MenuItem rounded="lg" value="create-brand" onClick={onCreateBrand} cursor="pointer">
          <Flex align="center" gap={3}>
            <Plus width={16} height={16} />
            <Text>{t('brandMenu.createBrand')}</Text>
          </Flex>
        </MenuItem>
      )}

      <MenuItem rounded="lg" value="invite-members" onClick={handleInviteMembers} cursor="pointer">
        <Flex align="center" gap={3}>
          <UserPlus width={16} height={16} />
          <Text>{t('brandMenu.inviteMembers')}</Text>
        </Flex>
      </MenuItem>

      <MenuItem rounded="lg" value="brand-settings" onClick={handleBrandSettings} cursor="pointer">
        <Flex align="center" gap={3}>
          <Settings width={16} height={16} />
          <Text>{t('brandMenu.brandSettings')}</Text>
        </Flex>
      </MenuItem>
    </MenuContent>
  );
};

export default BrandMenu;
